import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Badge } from '../ui/badge';

export const STAT_TYPES = ['stat', 'level', 'score'];

export const labelOf = (k) => {
  const s = String(k).replace(/_/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const textOf = (v) => {
  if (v == null) return '';
  if (Array.isArray(v)) return v.map(textOf).filter(Boolean).join(', ');
  if (typeof v === 'object') return Object.entries(v).map(([k, x]) => `${labelOf(k)}: ${textOf(x)}`).filter(Boolean).join('; ');
  return String(v);
};

const TONES = [
  ['very hard', 'text-red-600 dark:text-red-400'], ['hard', 'text-red-600 dark:text-red-400'], ['high', 'text-red-600 dark:text-red-400'],
  ['scarce', 'text-red-600 dark:text-red-400'], ['medium', 'text-amber-600 dark:text-amber-400'], ['moderate', 'text-amber-600 dark:text-amber-400'],
  ['low', 'text-emerald-600 dark:text-emerald-400'], ['easy', 'text-emerald-600 dark:text-emerald-400'], ['abundant', 'text-emerald-600 dark:text-emerald-400'],
];
export const levelTone = (v) => (TONES.find(([k]) => String(v || '').toLowerCase().startsWith(k)) || [null, ''])[1];

const isUrl = (s) => typeof s === 'string' && /^https?:\/\//i.test(s.trim());

export const CellValue = ({ value }) => (isUrl(value) ? (
  <a href={value.trim()} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 break-all">
    {value.replace(/^https?:\/\/(www\.)?/i, '').slice(0, 48)} <ExternalLink className="h-3 w-3 shrink-0" />
  </a>
) : <>{textOf(value)}</>);

const Bullets = ({ items, tone, numbered }) => (
  <ul className="space-y-1.5">
    {items.map((it, i) => (
      <li key={i} className="flex gap-2 text-sm leading-relaxed">
        <span className={`shrink-0 text-xs font-bold mt-0.5 w-4 ${tone}`}>{numbered ? `${i + 1}.` : '•'}</span>
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const ReportTable = ({ rows, columns }) => (
  <div className="overflow-x-auto rounded-lg border">
    <table className="w-full text-sm">
      <thead className="bg-muted/50">
        <tr>{columns.map(([k, l]) => <th key={k} className="text-left text-[11px] font-semibold text-muted-foreground px-3 py-2 whitespace-nowrap">{l}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t align-top">
            {columns.map(([k], ci) => (
              <td key={k} className={`px-3 py-2 ${ci === 0 ? 'font-medium' : 'text-muted-foreground'} ${k === 'severity' || k === 'weight' ? levelTone(r[k]) : ''}`}>
                <CellValue value={r[k]} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ReportCards = ({ rows, titleKey, fields }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    {rows.map((r, i) => (
      <div key={i} className="rounded-lg border bg-muted/20 p-3 space-y-1">
        <p className="text-sm font-semibold">{textOf(r[titleKey]) || '—'}</p>
        {fields.filter(([k]) => textOf(r[k])).map(([k, l]) => (
          <p key={k} className="text-xs leading-relaxed">
            <span className="text-muted-foreground">{l}: </span>
            <span className={k === 'difficulty' ? `font-semibold ${levelTone(r[k])}` : ''}><CellValue value={r[k]} /></span>
          </p>
        ))}
      </div>
    ))}
  </div>
);

const Pairs = ({ rows, pair: [a, b] }) => (
  <div className="space-y-2">
    {rows.map((r, i) => (
      <div key={i} className="rounded-lg border-l-2 border-primary/40 bg-muted/20 px-3 py-2">
        <p className="text-sm font-medium">“{textOf(r[a])}”</p>
        <p className="text-sm text-muted-foreground mt-0.5">{textOf(r[b])}</p>
      </div>
    ))}
  </div>
);

const KeyValues = ({ value }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
    {Object.entries(value).map(([k, v]) => (
      <div key={k} className="rounded-md border p-2">
        <p className="text-[10px] text-muted-foreground">{labelOf(k)}</p>
        <p className="text-xs font-semibold mt-0.5">{textOf(v)}</p>
      </div>
    ))}
  </div>
);

const RENDER = {
  lead: (f) => <p className="text-sm font-medium leading-relaxed">{textOf(f.value)}</p>,
  text: (f) => <p className="text-sm text-muted-foreground leading-relaxed">{textOf(f.value)}</p>,
  list: (f) => <Bullets items={f.value} tone="text-primary" />,
  numbered: (f) => <Bullets items={f.value} tone="text-primary" numbered />,
  good: (f) => <Bullets items={f.value} tone="text-emerald-600" />,
  bad: (f) => <Bullets items={f.value} tone="text-red-600" />,
  chips: (f) => <div className="flex flex-wrap gap-1.5">{f.value.map((c, i) => <Badge key={i} variant="secondary" className="text-xs font-normal">{c}</Badge>)}</div>,
  kv: (f) => <KeyValues value={f.value} />,
  table: (f) => <ReportTable rows={f.value} columns={f.columns} />,
  cards: (f) => <ReportCards rows={f.value} titleKey={f.title_key} fields={f.fields} />,
  pairs: (f) => <Pairs rows={f.value} pair={f.pair} />,
};

export const ReportField = ({ field }) => (
  <div className="space-y-1.5" data-testid={`report-field-${field.key}`}>
    {field.label && <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">{field.label}</p>}
    {(RENDER[field.type] || RENDER.text)(field)}
  </div>
);

const ScoreValue = ({ value }) => {
  const tone = value >= 70 ? 'text-red-600 dark:text-red-400' : value >= 45 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400';
  return (
    <div className={`mt-1 ${tone}`}>
      <p className="text-xl font-bold tabular-nums">{value}<span className="text-xs text-muted-foreground font-normal"> / 100</span></p>
      <div className="h-1.5 rounded-full bg-muted mt-1 overflow-hidden"><div className="h-full rounded-full bg-current" style={{ width: `${value}%` }} /></div>
    </div>
  );
};

export const StatTiles = ({ fields }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
    {fields.map((f) => (
      <div key={f.key} className="rounded-lg border bg-muted/30 p-3" data-testid={`report-stat-${f.key}`}>
        <p className="text-[11px] text-muted-foreground">{f.label}</p>
        {f.type === 'score' ? <ScoreValue value={f.value} /> : (
          <p className={`text-sm font-semibold mt-0.5 leading-snug ${f.type === 'level' ? levelTone(f.value) : ''}`}>{textOf(f.value)}</p>
        )}
      </div>
    ))}
  </div>
);
